import React, { useEffect, useMemo, useState } from "react";

import { Container } from "@material-ui/core";

import { useQuery } from "react-query";

import {
  Category,
  Product,
  Client,
  ProductModal,
  BlockedModal,
} from "../../components";

import { getCategories, getProducts } from "../../services";

import { useStore } from "../../store";

import {
  CategoryArray,
  ProductArray,
  Product as ProductInterface,
  Sector as SectorType,
} from "../../types";

import useStyles from "./styles";

import {
  getFirebaseRealTimeDatabase,
  getRealTimeProducts,
} from "../../services/firebaseRealTimeDatabase";
import { Sector } from "../../components/Sector";
import {
  useGetClient,
  useGetEntity,
  useGetSectorsActive,
} from "../../api/orders/hooks";
import { useEntity } from "../../hooks/useEntity";
import { getTerminalId } from "../../services";
import {
  getDefaultSectorId,
  getScopedSectors,
  matchesSectorScope,
  shouldShowSectorOthers,
} from "../../utils/sectorScope";

export const ManualPurchase: React.FC = () => {
  const { userManager, userTotem } = useStore();

  const [openProductModal, setOpenProductModal] = useState<boolean>(false);
  const [openBlockedModal, setOpenBlockedModal] = useState<boolean>(false);

  const [onShowSectors, setOnShowSectors] = useState<boolean>(false);
  const [sectorsList, setSectorsList] = useState<any[]>([]);
  const [categories, setCategories] = useState<any>([]);
  const [sectorId, setSectorId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("0");
  const [message, setMessage] = useState<string>("");
  const [selectedProduct, setSelectedProduct] =
    useState<ProductInterface | null>(null);
  const [quantitySectors, setQuantitySectors] = useState(0);
  const [realTimeProducts, setRealTimeProducts] = useState<ProductInterface[]>(
    [],
  );

  const Styles = useStyles({ onShowSectors, quantitySectors });

  const handleClickInProduct = (product: ProductInterface) => {
    setSelectedProduct(product);

    setOpenProductModal(true);
  };

  const closeModal = () => {
    setOpenProductModal(false);

    setSelectedProduct(null);
  };

  const { data: entity } = useGetEntity(userManager.entity, true);
  const { allowStudentCardMachinePurchase } = useEntity();
  const canBypassBalanceBlock =
    allowStudentCardMachinePurchase &&
    !!getTerminalId() &&
    userTotem.wallet.period === "";
  const canUseNegativeBalance =
    entity?.allowPurchaseWithNegativeBalance && userTotem.wallet.period === "";

  const isBlockedProduct = (price: number): boolean => {
    if (canUseNegativeBalance || canBypassBalanceBlock) {
      return false;
    }

    return (
      price / 100 > userTotem.wallet.periodLimit - userTotem.wallet.spent &&
      !!userTotem.id
    );
  };

  const { data: sectors } = useGetSectorsActive(userManager.entity, true);
  const scopedSectors = useMemo(
    () => getScopedSectors(sectors, userManager.segmentId),
    [sectors, userManager.segmentId],
  );
  const hasFixedSegment = !!userManager.segmentId;
  const { data: categoryData, isLoading: loadingCategory } = useQuery<
    CategoryArray,
    Error
  >("categoriesData", () => getCategories(userManager.entity));
  const { data: client } = useGetClient(userTotem.id, true);
  const {
    data: productsData,
    isLoading: loadingProducts,
    refetch: getProductsQuery,
  } = useQuery<ProductArray, Error>(
    ["productsData", userManager.entity, userTotem.id],
    () => getProducts(userManager.entity, userTotem.id),
  );

  useEffect(() => {
    const unsubscribe = getRealTimeProducts(
      userManager.entity,
      (products: ProductInterface[]) => {
        setRealTimeProducts(products);
      },
    );
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userManager.entity]);

  const getProductWithRealTimeQuantity = (product: ProductInterface) => {
    const realTimeProduct = realTimeProducts.find((p) => p.id === product.id);
    if (realTimeProduct) {
      return { ...product, quantity: realTimeProduct.quantity };
    }
    return product;
  };

  const filterProducts = () => {
    let products =
      productsData?.filter((product) => !product.isvariablePrice) ?? [];

    if (
      entity?.discountForEmployeesChildren &&
      client?.admins?.some((a: any) => a.user.role === "staff")
    ) {
      const updatedProducts = products?.map((p) => ({
        ...p,
        price:
          p.discountPrice !== undefined && p.discountPrice > 0
            ? p.discountPrice
            : p.price,
        priceWithoutDiscount: p.price,
      }));
      products = updatedProducts;
    }

    const filterByCategory = (product: any) =>
      product?.productCategory?.id === selectedCategory;
    const filterBySector = (product: any) =>
      matchesSectorScope(product.segment, sectorId, userManager.segmentId);
    const filterByHighlight = (product: any) => product.isHighlighted;
    const filterByNoSegment = (product: any) => !product.segment;

    if (onShowSectors) {
      if (selectedCategory !== "0") {
        if (isSectorSelectedOthers(sectorId)) {
          return products
            .filter(
              (product) =>
                filterByNoSegment(product) && filterByCategory(product),
            )
            .map(getProductWithRealTimeQuantity);
        }
        return products
          .filter(
            (product) => filterByCategory(product) && filterBySector(product),
          )
          .map(getProductWithRealTimeQuantity);
      }

      if (isSectorSelectedOthers(sectorId)) {
        return products
          .filter(
            (product) =>
              filterByHighlight(product) && filterByNoSegment(product),
          )
          .map(getProductWithRealTimeQuantity);
      }
      return products
        .filter(
          (product) => filterByHighlight(product) && filterBySector(product),
        )
        .map(getProductWithRealTimeQuantity);
    }

    if (selectedCategory !== "0") {
      return products
        .filter(filterByCategory)
        .map(getProductWithRealTimeQuantity);
    }

    return products
      .filter(filterByHighlight)
      .map(getProductWithRealTimeQuantity);
  };

  const isSectorSelectedOthers = (id: string) =>
    shouldShowSectorOthers(userManager.segmentId) && id === "others";

  const filterProductsSectors = (id?: string) => {
    if (id && productsData && categoryData) {
      setSelectedCategory("0");
      let filteredProducts: any[] = [];
      if (isSectorSelectedOthers(id)) {
        filteredProducts = productsData.filter((prod: any) => !prod.segment);
      } else {
        filteredProducts = productsData.filter(
          (prod: any) =>
            matchesSectorScope(prod.segment, id, userManager.segmentId) &&
            !prod.isvariablePrice,
        );
      }

      const uniqueCategoryIds = new Set(
        filteredProducts.map((p: any) => p.productCategory.id),
      );
      const uniqueCategories = categoryData.filter((cat: any) =>
        uniqueCategoryIds.has(cat.id),);
      setCategories(uniqueCategories);
    }
    return [];
  };

  const handleClickProduct = (product: ProductInterface) => {
    if (!product.isLocked && !isBlockedProduct(product.price)) {
      if (product.quantity === 0) {
        setMessage("Esse item não tem mais estoque");
        setOpenBlockedModal(true);
      } else {
        handleClickInProduct(product);
      }
    }
  };

  const close = () => {
    setOpenBlockedModal(false);
    setMessage("");
  };

  const refresh = () => {
    if (userTotem.id) {
      getProductsQuery();
    }
  };

  const onSectorsList = () => {
    const secList = scopedSectors.map((s: SectorType, i: number) => ({
      ...s,
      selected: i === 0,
    }));
    secList.sort((a: SectorType, b: SectorType) =>
      a.name.localeCompare(b.name),);
    if (shouldShowSectorOthers(userManager.segmentId)) {
      secList.push({
        id: "others",
        name: "Outros",
        isActive: true,
        selected: false,
        products: [],
      });
    }
    setSectorsList(secList);
  };

  useEffect(() => {
    if (
      scopedSectors.length > 0 &&
      onShowSectors &&
      categoryData &&
      productsData &&
      categoryData?.length > 0 &&
      productsData?.length > 0
    ) {
      onSectorsList();
      setQuantitySectors(scopedSectors.length);
      if (!sectorId) {
        const defaultSectorId = getDefaultSectorId(
          scopedSectors,
          userManager.segmentId,
        );
        setSectorId(defaultSectorId);
        filterProductsSectors(defaultSectorId);
      }
    }
  }, [
    scopedSectors,
    categoryData,
    productsData,
    onShowSectors,
    sectorId,
    userManager.segmentId,
  ]);

  useEffect(() => {
    if (
      hasFixedSegment &&
      userManager.segmentId &&
      categoryData &&
      productsData
    ) {
      setOnShowSectors(true);
      setSectorId(userManager.segmentId);
      filterProductsSectors(userManager.segmentId);
    }
  }, [hasFixedSegment, userManager.segmentId, categoryData, productsData]);

  useEffect(() => {
    if (categoryData && !onShowSectors) {
      setCategories(categoryData);
    }
  }, [categoryData, onShowSectors]);

  useEffect(() => {
    if (
      (entity?.isSectorsOnThePurchaseTotem || hasFixedSegment) &&
      scopedSectors.length > 0
    ) {
      setOnShowSectors(true);
    }
  }, [entity, scopedSectors, hasFixedSegment]);

  useEffect(() => {
    getFirebaseRealTimeDatabase(userManager.entity, refresh);
  }, []);

  return (
    <Container className={Styles.container} maxWidth={false}>
      {onShowSectors && (
        <>
          <div className={Styles.sectors}>
            <div className={Styles.sectorsContainer}>
              {sectorsList?.map((sec) => (
                <Sector
                  {...sec}
                  key={sec.id}
                  name={sec.name}
                  onClick={(id) => filterProductsSectors(id) && setSectorId(id)}
                  selected={sec.id === sectorId}
                />
              ))}
            </div>
            <div className={Styles.divider} />
          </div>
        </>
      )}

      {!loadingCategory && categories.length > 0 && (
        <div className={Styles.categories}>
          <Category
            id="0"
            picture=""
            name="Destaques"
            onClick={(id) => setSelectedCategory(id)}
            selected={selectedCategory === "0"}
          />
          {categories
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((category: any) => (
              <Category
                key={category.id}
                {...category}
                onClick={(id) => setSelectedCategory(id)}
                selected={category.id === selectedCategory}
              />
            ))}
        </div>
      )}

      <Client onShowSectors={onShowSectors} />

      <Container className={Styles.content} maxWidth={false}>
        {!loadingProducts && (
          <div className={Styles.products}>
            {filterProducts().map((product) => (
              <Product
                key={product.id}
                {...product}
                isLocked={
                  isBlockedProduct(product.price) ||
                  product.isLocked ||
                  product.quantity === 0
                }
                isPriceWithoutDiscount={
                  product.priceWithoutDiscount !== undefined &&
                  product.priceWithoutDiscount > 0 &&
                  product.discountPrice !== undefined &&
                  product.discountPrice > 0
                }
                priceWithoutDiscount={product.priceWithoutDiscount}
                onClick={() => handleClickProduct(product)}
              />
            ))}
          </div>
        )}
      </Container>

      <BlockedModal message={message} open={openBlockedModal} onClose={close} />

      {selectedProduct && (
        <ProductModal
          open={openProductModal}
          onClose={closeModal}
          product={selectedProduct}
        />
      )}
    </Container>
  );
};
